import { Box, Button, Typography } from '@mui/material';
import { useEffect, useRef, useState, type ChangeEvent, type ReactElement } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import healtheraLogo from 'app/assets/healthera_logo.png';
import useHeaderActions from 'app/hooks/useHeaderActions';
import useHeaderTitle from 'app/hooks/useHeaderTitle';
import useNavigationHeader from 'app/hooks/useNavigationHeader';
import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';
import ErrorState from 'app/shared-components/common/ErrorState';

import { useUpdateWebsiteLogo } from './hooks/useUpdateWebsiteLogo';

const LogoEditPage = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';
  const organisationId = searchParams.get('organisationId') ?? '';
  const currentLogoUrl = (location.state as { logoUrl?: string } | null)?.logoUrl ?? healtheraLogo;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { mutate: updateLogo, isLoading: isSaving } = useUpdateWebsiteLogo();

  const themePath = `/plus-websites/theme-and-styles?${new URLSearchParams({ organisationId })}`;

  function handleSave() {
    if (!selectedFile) return;
    updateLogo({ websiteId, file: selectedFile }, { onSuccess: () => navigate(themePath) });
  }

  useNavigationHeader({ title: 'Back', navigateTo: themePath });
  useHeaderTitle('Logo');
  useHeaderActions([
    { title: 'Save', type: 'button', callback: handleSave, disabled: !selectedFile || isSaving },
  ]);

  // Release the object URL when it is replaced or the page unmounts.
  useEffect(() => () => URL.revokeObjectURL(previewUrl ?? ''), [previewUrl]);

  if (!websiteId) {
    return (
      <ErrorState
        title="Missing required parameters"
        subtitle="Cannot edit the logo without a valid website."
      />
    );
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so re-selecting the same file still fires a change event.
    event.target.value = '';
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  return (
    <MarketingStationLayout className="vtwo:flex vtwo:flex-col vtwo:h-full vtwo:overflow-hidden">
      <Box className="vtwo:flex-1 vtwo:bg-plus-canvas vtwo:overflow-y-auto">
        <Box className="vtwo:max-w-2xl vtwo:mx-auto vtwo:py-12 vtwo:px-8">
          <Box className="vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white vtwo:p-6">
            <Typography className="vtwo:text-lg vtwo:font-bold vtwo:text-typography-primary vtwo:mb-4">
              Logo
            </Typography>
            <Typography className="vtwo:text-typography-primary vtwo:mb-2">Main logo</Typography>
            <Box className="vtwo:flex vtwo:items-center vtwo:justify-center vtwo:rounded-lg vtwo:border vtwo:border-plus-border vtwo:bg-white vtwo:py-8 vtwo:mb-4">
              <img
                src={previewUrl ?? currentLogoUrl}
                alt="Website logo"
                className="vtwo:h-10 vtwo:object-contain"
              />
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="vtwo:hidden"
              onChange={handleFileChange}
            />
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                color: '#374151',
                borderColor: '#D1D5DB',
                '&:hover': { borderColor: '#9CA3AF', backgroundColor: 'rgba(0, 0, 0, 0.02)' },
              }}
            >
              Change logo
            </Button>
          </Box>
        </Box>
      </Box>
    </MarketingStationLayout>
  );
};

export default LogoEditPage;
